import {
  Button,
  Card,
  Dropdown,
  DropdownDivider,
  DropdownItem,
  FlagProps,
  Flags,
  IconButton,
  openDeleteDialog,
  Parameter,
  Parameters,
  Segment,
  Separator,
  Tooltip,
} from 'platform/components';
import {
  Box,
  Clickable,
  Heading,
  Hide,
  HStack,
  Icon,
  Show,
  Space,
  Text,
  useResponsivePropValue,
  VStack,
} from 'platform/foundation';
import {CurrencyFormat, useDateTimeFormatter} from 'platform/locale';
import {match} from 'ts-pattern';

import {RefObject} from 'react';

import {defaultTo, find, isNil} from 'ramda';
import {isNilOrEmpty, isNotNil, isNotNilOrEmpty, isPositive} from 'ramda-adjunct';

import {
  useGetMetadaServiceCaseQuery,
  useGetOrderJobItemsTotalAmountQuery,
  useGetServiceCaseJobItemsCountQuery,
  useGetServiceCaseOrderVariantsQuery,
  useGetServiceJobActionsQuery,
  useGetServiceOrderQuery,
  useGetServiceOrderVariantQuery,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {rentalOrderRoutes} from '@dms/routes';
import {useTenant, DEFAULT_PRICE, queryParams, usePermissions} from '@dms/shared';
import {JobForm, JobFormProps, JobFormRef, JobType} from '@dms/shared-aftersales';

import {
  buildArray,
  composePath,
  DOT_CHARACTER,
  Nullish,
  parseDate,
  suffixTestId,
  TestIdProps,
  useQueryState,
} from 'shared/web';

import {JobSegments} from '../../types/JobSegments';
import {getSparePartStateFlag} from '../../utils/getSparePartStateFlag';
import {getTimeTrackingStateFlag} from '../../utils/getTimeTrackingStateFlag';
import {AssignMechanic} from './components/AssignMechanic';
import {FullyReturnedItemsDataGrid} from './components/FullyReturnedItemsDataGrid';
import {JobItemsDataGrid} from './components/JobItemsDataGrid';
import {JobTrackedTime} from './components/JobTrackedTime';
import {ServicePackageDataGrid} from './components/ServicePackageDataGrid';
import {JobAction} from './types/JobAction';

interface JobProps extends TestIdProps {
  serviceCaseId: string;
  jobData?: JobType | Nullish;
  orderId?: string;
  actions?: JobAction[];
  rejectedReason?: string;
  onReject?: (jobId: string) => void;
  onActivate?: (jobId: string) => void;
  onRemove?: (jobId: string) => Promise<void>;
  onSubmit: JobFormProps['onSubmit'];
  isReadOnly?: boolean;
  datagridKey?: string;
  isReportButtonDisabled?: boolean;
  formRef?: RefObject<JobFormRef | null>;
}

const MOBILE_MAX_DESCRIPTION_LENGTH = 30;
const TABLET_MAX_DESCRIPTION_LENGTH = 40;
const NOTEBOOK_MAX_DESCRIPTION_LENGTH = 50;
const DESKTOP_MAX_DESCRIPTION_LENGTH = 100;

export function Job(props: JobProps) {
  const formatDateTime = useDateTimeFormatter();
  const tenant = useTenant();
  const [addLabourJobId, setAddLabourJobId] = useQueryState(
    queryParams.SERVICE_CASE_ADD_LABOUR_JOB_ID
  );
  const [addMaterialRequestJobId, setAddMaterialRequestJobId] = useQueryState(
    queryParams.SERVICE_CASE_ADD_MATERIAL_REQUEST_JOB_ID
  );
  const [activeSegment, setActiveSegment] = useQueryState(queryParams.SERVICE_CASE_JOB_SEGMENT);

  const currentSegment = (activeSegment || JobSegments.WORK_ITEMS) as JobSegments;

  const {data: serviceCase} = useGetMetadaServiceCaseQuery({serviceCaseId: props.serviceCaseId});

  const {data: serviceOrder} = useGetServiceOrderQuery(
    {
      serviceCaseId: props.serviceCaseId,
      serviceOrderId: props.orderId ?? '',
    },
    {
      skip: isNilOrEmpty(props.orderId) || isNilOrEmpty(props.serviceCaseId),
    }
  );

  const {data: serviceOrderVariant} = useGetServiceOrderVariantQuery(
    {serviceOrderVariantId: serviceOrder?.serviceOrderVariantId ?? ''},
    {skip: isNilOrEmpty(serviceOrder?.serviceOrderVariantId)}
  );

  const [canReadChronos, canMaterialDispatch, canMaterialNonBinding, canMaterialRequest] =
    usePermissions({
      permissionKeys: ['chronosUser', 'materialDispatch', 'materialNonBinding', 'materialRequest'],
      scopes: {
        materialDispatch: {
          branchId: serviceCase?.branchId ?? '',
          serviceCaseType: serviceCase?.serviceCaseTypeId ?? '',
          serviceOrderType: serviceOrderVariant?.serviceOrderTypeId ?? '',
        },
        materialNonBinding: {
          branchId: serviceCase?.branchId ?? '',
          serviceCaseType: serviceCase?.serviceCaseTypeId ?? '',
          serviceOrderType: serviceOrderVariant?.serviceOrderTypeId ?? '',
        },
        materialRequest: {
          branchId: serviceCase?.branchId ?? '',
          serviceCaseType: serviceCase?.serviceCaseTypeId ?? '',
          serviceOrderType: serviceOrderVariant?.serviceOrderTypeId ?? '',
        },
      },
    });

  const {orderVariant} = useGetServiceCaseOrderVariantsQuery(
    {serviceCaseId: props.serviceCaseId},
    {
      selectFromResult: ({data}) => ({
        orderVariant: find((variant) => variant?.id === props.jobData?.orderVariantId, data ?? []),
      }),
      skip: !props.jobData?.orderVariantId || !!props.orderId,
    }
  );
  const {data: totalAmount} = useGetOrderJobItemsTotalAmountQuery(
    {
      serviceCaseId: props.serviceCaseId,
      serviceJobId: props.jobData?.id ?? '',
      serviceOrderId: props.orderId ?? '',
    },
    {
      skip: !props.jobData?.id || !props.orderId,
    }
  );
  const {data: actions} = useGetServiceJobActionsQuery(
    {
      serviceCaseId: props.serviceCaseId,
      serviceJobId: props.jobData?.id ?? '',
      serviceOrderId: props.orderId ?? '',
    },
    {
      skip: !props.jobData?.id || !props.orderId,
    }
  );
  const {data: jobItemsCount} = useGetServiceCaseJobItemsCountQuery(
    {
      serviceCaseId: props.serviceCaseId,
      serviceJobId: props.jobData?.id ?? '',
    },
    {
      skip: !props.jobData?.id,
    }
  );

  const [opendJobId, setOpendJobId, clearOpenJobId] = useQueryState(
    queryParams.SERVICE_CASE_OPEN_JOB_ID
  );

  const isOpen = isNotNilOrEmpty(props.jobData?.id) && opendJobId === props.jobData?.id;

  const dropdownIcon = isOpen ? 'navigation/expand_less' : 'navigation/expand_more';

  const toggleOpen = () => {
    if (isOpen) {
      return clearOpenJobId();
    }
    setOpendJobId(props.jobData?.id ?? '');
  };

  const handleOpenDeleteDialog = () => {
    if (!props.onRemove) {
      return;
    }

    openDeleteDialog({
      onConfirm: () => props.onRemove?.(props.jobData?.id || ''),
      text: i18n.t('entity.orderRequest.actions.delete', {name: props.jobData?.name}),
      'data-testid': suffixTestId('removeJob', props),
    });
  };

  const handleOpenAddLabour = () => {
    if (props.jobData?.id) {
      setAddLabourJobId(props.jobData?.id);
    }
  };

  const handleOpenAddMaterialRequest = () => {
    if (props.jobData?.id) {
      setAddMaterialRequestJobId(props.jobData?.id);
    }
  };

  const sparePartFlag = getSparePartStateFlag(props.jobData?.sparePartWarehouseState);
  const timeTrackingFlag = getTimeTrackingStateFlag(props.jobData?.timeTrackingState);
  const flags = buildArray<FlagProps>()
    .when(isNotNil(timeTrackingFlag) && canReadChronos, timeTrackingFlag!)
    .when(isNotNil(sparePartFlag), sparePartFlag!)
    .when(props.jobData?.isCarRental, {
      label: i18n.t('entity.rentalOrder.labels.rental'),
      colorScheme: 'yellow',
      isSubtle: true,
    })
    .when(isNotNil(props.jobData?.carRentalDetails?.state), {
      label: i18n.t(`entity.rentalOrder.states.${props.jobData?.carRentalDetails?.state}`),
      colorScheme: 'blue',
      isSubtle: true,
    })
    .when(props.jobData?.isServicePackage, {
      label: i18n.t('entity.workshopServicePackages.labels.servicePackage'),
      colorScheme: 'blue',
    })
    .when(props.jobData?.isServiceCampaign, {
      label: i18n.t('entity.campaigns.labels.serviceCampaign'),
      colorScheme: 'yellow',
    })
    .when(props.jobData?.repeatedRepair, {
      label: i18n.t('entity.workshop.labels.repeatedRepair'),
      colorScheme: 'red',
      isSubtle: true,
    })
    .when(props.jobData?.partsRequest && props.jobData.partsReady, {
      label: i18n.t('entity.workshop.labels.partsReady'),
      colorScheme: 'blue',
      isSubtle: true,
    })
    .when(props.jobData?.partsRequest && !props.jobData.partsReady, {
      label: i18n.t('entity.workshop.labels.partsRequest'),
      colorScheme: 'orange',
      isSubtle: true,
    });

  const maxDescriptionLength = useResponsivePropValue([
    MOBILE_MAX_DESCRIPTION_LENGTH,
    TABLET_MAX_DESCRIPTION_LENGTH,
    NOTEBOOK_MAX_DESCRIPTION_LENGTH,
    DESKTOP_MAX_DESCRIPTION_LENGTH,
  ]);
  const shouldShowDescriptionTooltip =
    (props.jobData?.description?.length ?? 0) > maxDescriptionLength;
  const shouldShowRentalOrderTooltip =
    (props.jobData?.carRentalDetails?.note?.length ?? 0) > maxDescriptionLength;

  const parameters = buildArray<Parameter>()
    .when(props.jobData?.description, {
      title:
        (props.jobData?.description ?? '').slice(0, maxDescriptionLength) +
        (shouldShowDescriptionTooltip ? '...' : ''),
      icon: 'action/description',
      tooltip: shouldShowDescriptionTooltip ? (
        <>
          <Text alternative color="white" size="xSmall">
            {i18n.t('general.labels.description')}
          </Text>
          <Text color="white" size="xSmall">
            {props.jobData?.description}
          </Text>
        </>
      ) : undefined,
    })
    .when(props.jobData?.isCarRental && props.jobData?.carRentalDetails?.rentalOrderId, {
      variant: 'link',
      title: i18n.t('entity.rentalOrder.labels.detail'),
      icon: 'action/open_in_new',
      onClick: (e) => {
        e.stopPropagation();
        const path = composePath(rentalOrderRoutes.vehicleAndCustomer, {
          params: {id: props.jobData?.carRentalDetails?.rentalOrderId},
          isSearchParamsPreserved: false,
        });
        window.open(path, '_blank');
      },
    })
    .when(props.jobData?.isCarRental && props.jobData?.carRentalDetails?.note, {
      title:
        (props.jobData?.carRentalDetails?.note ?? '').slice(0, maxDescriptionLength) +
        (shouldShowRentalOrderTooltip ? '...' : ''),
      icon: 'sidebar/edit',
      tooltip: (
        <>
          <Text alternative color="white" size="xSmall">
            {i18n.t('entity.rentalOrder.labels.note')}
          </Text>
          {shouldShowRentalOrderTooltip && (
            <Text color="white" size="xSmall">
              {props.jobData?.carRentalDetails?.note}
            </Text>
          )}
        </>
      ),
    })
    .add(props.jobData?.serviceCampaignDetails?.defectCode)
    .when(
      props.jobData?.serviceCampaignDetails?.startDate,
      props.jobData?.serviceCampaignDetails?.startDate &&
        `${i18n.t('general.labels.from')}: ${formatDateTime(
          'dateShort',
          parseDate(props.jobData?.serviceCampaignDetails?.startDate)
        )}`
    )
    .when(
      props.jobData?.serviceCampaignDetails?.dueDate,
      props.jobData?.serviceCampaignDetails?.dueDate &&
        `${i18n.t('general.labels.to')}: ${formatDateTime(
          'dateShort',
          parseDate(props.jobData?.serviceCampaignDetails?.dueDate)
        )}`
    )
    .add(props.jobData?.servicePackageDetails?.number)
    .add(orderVariant?.name);

  const totalPriceWithVat = defaultTo(
    DEFAULT_PRICE,
    match(props.jobData?.isFixedPrice)
      .with(
        true,
        () =>
          props.jobData?.fixedPrice?.withVat?.amount ??
          totalAmount?.totalPrice?.withVat?.amount ??
          DEFAULT_PRICE
      )
      .otherwise(() => (props.orderId ? totalAmount?.totalPrice?.withVat?.amount : DEFAULT_PRICE))
  );

  const totalPriceWithVatCurrency = defaultTo(
    tenant.tenantCurrency,
    match(props.jobData?.isFixedPrice)
      .with(
        true,
        () =>
          props.jobData?.fixedPrice?.withVat?.currency ??
          totalAmount?.totalPrice?.withVat?.currency ??
          tenant.tenantCurrency
      )
      .otherwise(() =>
        props.orderId ? totalAmount?.totalPrice?.withVat?.currency : tenant.tenantCurrency
      )
  );

  const totalPriceWithoutVat = defaultTo(
    DEFAULT_PRICE,
    match(props.jobData?.isFixedPrice)
      .with(
        true,
        () =>
          props.jobData?.fixedPrice?.withoutVat?.amount ??
          totalAmount?.totalPrice?.withoutVat?.amount ??
          DEFAULT_PRICE
      )
      .otherwise(() =>
        props.orderId ? totalAmount?.totalPrice?.withoutVat?.amount : DEFAULT_PRICE
      )
  );

  const totalPriceWithoutVatCurrency = defaultTo(
    tenant.tenantCurrency,
    match(props.jobData?.isFixedPrice)
      .with(
        true,
        () =>
          props.jobData?.fixedPrice?.withoutVat?.currency ??
          totalAmount?.totalPrice?.withoutVat?.currency ??
          tenant.tenantCurrency
      )
      .otherwise(() =>
        props.orderId ? totalAmount?.totalPrice?.withoutVat?.currency : tenant.tenantCurrency
      )
  );

  const hasAnyMaterialPermission =
    canMaterialDispatch || canMaterialNonBinding || canMaterialRequest;

  const canAddLabour = props.jobData?.allowedActions
    ? props.jobData.allowedActions.includes('ADD_LABOUR')
    : true;
  const canAddMaterial = props.jobData?.allowedActions
    ? props.jobData.allowedActions.includes('ADD_MATERIAL') && hasAnyMaterialPermission
    : true;
  const canAssignMechanic = props.jobData?.allowedActions
    ? props.jobData.allowedActions.includes('ASSIGN_MECHANIC')
    : true;
  const showAddButtons = !props.jobData?.isCarRental && (canAddLabour || canAddMaterial);

  return (
    <Card variant={props.jobData?.isUncategorized ? 'inlineWhite' : 'inlineGrey'}>
      <Box data-testid={suffixTestId('head', props)}>
        <HStack align="center" height="100%">
          <Box flex={1}>
            <Clickable onClick={toggleOpen}>
              <VStack align="flex-start">
                <HStack spacing={2} align="center">
                  <Heading size={5} data-testid={suffixTestId('name', props)}>
                    {props.jobData?.name}
                  </Heading>
                  <Flags size="small" flags={flags} data-testid={suffixTestId('state', props)} />
                </HStack>
                <Hide when={props.jobData?.isUncategorized}>
                  <HStack spacing={1} align="center" height={6}>
                    <Show
                      when={
                        isNotNil(props.orderId) && canAssignMechanic && !props.jobData?.isCarRental
                      }
                    >
                      <AssignMechanic
                        assignMechanics={props.jobData?.assignMechanics}
                        jobId={props.jobData?.id ?? ''}
                        competenceId={props.jobData?.competencyId ?? ''}
                        data-testid={suffixTestId('assignMechanic', props)}
                        isDisabled={
                          props.jobData?.isCarRental || !canAssignMechanic || !!props.isReadOnly
                        }
                      />
                    </Show>
                    <Show
                      when={
                        isNotNil(props.orderId) &&
                        parameters.some(isNotNil) &&
                        !props.jobData?.isCarRental
                      }
                    >
                      <Text color="tertiary" size="xSmall">
                        {DOT_CHARACTER}
                      </Text>
                    </Show>
                    <Parameters
                      color="tertiary"
                      parameters={parameters}
                      data-testid={suffixTestId('parameters', props)}
                      size="xSmall"
                    />
                    <Show when={props.jobData?.isDoNotApplyDiscount || props.jobData?.isFixedPrice}>
                      <Text size="xSmall" color="tertiary">
                        {DOT_CHARACTER}
                      </Text>
                      <Icon value="action/info" size={4} color="severity.warning" />
                      <Text size="xSmall" color="warning">
                        {props.jobData?.isFixedPrice
                          ? i18n.t('entity.workshopServicePackages.labels.customPrice')
                          : i18n.t('entity.orderRequest.labels.noDiscounts')}
                      </Text>
                    </Show>
                    <Show when={props.jobData?.servicePackageDetails?.isLock}>
                      <Text size="xSmall" color="tertiary">
                        {DOT_CHARACTER}
                      </Text>
                      <Icon value="action/lock" size={4} color="severity.danger" />
                      <Text size="xSmall" color="danger">
                        {i18n.t('entity.serviceOrder.states.locked')}
                      </Text>
                    </Show>
                    <Show when={props.rejectedReason}>
                      <Text size="xSmall" color="tertiary">
                        {DOT_CHARACTER}
                      </Text>
                      <Tooltip label={props.rejectedReason}>
                        <Text size="xSmall" color="danger">
                          {i18n.t('entity.orderIntegration.labels.rejected')}
                        </Text>
                      </Tooltip>
                    </Show>
                  </HStack>
                </Hide>
                <Show when={props.jobData?.isUncategorized}>
                  <Text size="xSmall" color="secondary">
                    {i18n.t('entity.order.labels.uncategorizedDescription')}
                  </Text>
                </Show>
              </VStack>
            </Clickable>
          </Box>
          <HStack align="center" height="100%" spacing={4}>
            <Show when={props.orderId || props.jobData?.isFixedPrice}>
              <VStack spacing={1} align="flex-end">
                <Text
                  size="small"
                  alternative
                  data-testid={suffixTestId('totalPriceWithVat', props)}
                >
                  <CurrencyFormat
                    decimals={2}
                    number={totalPriceWithVat}
                    currency={totalPriceWithVatCurrency}
                  />
                </Text>
                <Text
                  size="xSmall"
                  color="secondary"
                  data-testid={suffixTestId('totalPriceWithoutVat', props)}
                >
                  <CurrencyFormat
                    decimals={2}
                    number={totalPriceWithoutVat}
                    currency={totalPriceWithoutVatCurrency}
                  />
                  {` ${i18n.t('general.labels.withoutVat')}`}
                </Text>
              </VStack>
            </Show>
            <Show when={canReadChronos}>
              <JobTrackedTime
                data-testid={suffixTestId('jobTrackedTime', props)}
                serviceCaseId={props.serviceCaseId}
                serviceOrderId={props.orderId}
                serviceCaseJobId={props.jobData?.id}
                estimatedTime={props.jobData?.workTimeEstimated}
                jobName={props.jobData?.name ?? ''}
                isDisabled={props.isReportButtonDisabled}
              />
            </Show>
            <HStack align="center" height="100%" spacing={2}>
              <Show when={actions && !props.isReadOnly}>
                <Dropdown
                  data-testid={suffixTestId('actions', props)}
                  dropdownControl={
                    <IconButton
                      icon="navigation/more_vert"
                      size="small"
                      isDisabled={!!props.jobData?.isCarRental}
                      data-testid={suffixTestId('more-actions', props)}
                    />
                  }
                  isDisabled={!!props.jobData?.isCarRental}
                >
                  {actions?.jobActions?.options?.filter(isNotNil).map((option, index) => {
                    const action = props.actions?.find((action) => action?.key === option?.key);

                    if (isNil(action)) {
                      return null;
                    }

                    return (
                      <Hide when={option.style === 'hidden'} key={option.key}>
                        <Show when={option.type === 'list-item' && isNotNil(action)}>
                          <DropdownItem
                            {...action}
                            isDisabled={option.style === 'disabled'}
                            data-testid={suffixTestId(`action-[${index}]`, props)}
                          />
                        </Show>
                        <Show when={option.type === 'divider'}>
                          <DropdownDivider />
                        </Show>
                      </Hide>
                    );
                  })}
                </Dropdown>
              </Show>
              <Show when={props.onRemove && !props.isReadOnly}>
                <IconButton
                  icon="content/remove_circle"
                  onClick={handleOpenDeleteDialog}
                  severity="danger"
                  size="small"
                  data-testid={suffixTestId('remove', props)}
                />
              </Show>
              <Show when={props.onReject && !props.isReadOnly}>
                <IconButton
                  icon="content/remove_circle"
                  onClick={() => props.onReject?.(props.jobData?.id ?? '')}
                  severity="danger"
                  size="small"
                  data-testid={suffixTestId('reject', props)}
                />
              </Show>
              <Show when={props.onActivate && !props.isReadOnly}>
                <IconButton
                  icon="navigation/refresh"
                  onClick={() => props.onActivate?.(props.jobData?.id ?? '')}
                  size="small"
                  data-testid={suffixTestId('activate', props)}
                />
              </Show>
              <IconButton
                icon={dropdownIcon}
                onClick={toggleOpen}
                size="small"
                data-testid={suffixTestId('expand', props)}
              />
            </HStack>
          </HStack>
        </HStack>
      </Box>
      <Show when={isOpen}>
        <Box paddingTop={props.jobData?.isCarRental ? 0 : 4}>
          <Hide when={props.jobData?.isUncategorized || props.jobData?.isCarRental}>
            <Hide when={!props.orderId}>
              <HStack justify="space-between" align="center">
                <Segment<JobSegments>
                  minWidth={35}
                  value={currentSegment}
                  onChange={setActiveSegment}
                  options={[
                    {
                      value: JobSegments.WORK_ITEMS,
                      label: `${i18n.t('entity.orderRequest.labels.workItems')} (${props.jobData?.serviceItems?.itemsCount ?? 0})`,
                    },
                    {
                      value: JobSegments.DETAILS,
                      label: i18n.t('entity.workshop.labels.requestDetails'),
                    },
                    {
                      value: JobSegments.FULLY_RETURNED,
                      label: `${i18n.t('entity.orderRequest.labels.fullyReturned')} (${props.jobData?.serviceItems?.fullyReturnedItemsCount ?? 0})`,
                      isDisabled: (props.jobData?.serviceItems?.fullyReturnedItemsCount ?? 0) === 0,
                    },
                  ]}
                  data-testid={suffixTestId('segment', props)}
                />
              </HStack>
            </Hide>

            <Show when={currentSegment === JobSegments.DETAILS || !props.orderId}>
              <Separator />
              <JobForm
                serviceCaseId={props.serviceCaseId}
                jobData={props.jobData}
                orderId={props.orderId}
                orderVariantId={serviceOrder?.serviceOrderVariantId ?? undefined}
                onSubmit={props.onSubmit}
                isReadOnly={props.isReadOnly}
                isLockedServicePackage={!!props.jobData?.servicePackageDetails?.isLock}
                formRef={props.formRef}
                data-testid={suffixTestId('jobForm', props)}
              />
            </Show>
          </Hide>

          <Show
            when={
              props.jobData?.isUncategorized ||
              props.jobData?.isCarRental ||
              currentSegment === JobSegments.WORK_ITEMS
            }
          >
            <Show when={isNil(props.orderId) && isPositive(jobItemsCount?.itemCount)}>
              <Separator />
              <ServicePackageDataGrid
                jobId={props.jobData?.id ?? ''}
                data-testid={suffixTestId('items', props)}
              />
            </Show>
            <Show when={props.orderId}>
              <Separator />
              <HStack justify="space-between" align="center">
                <Hide when={props.jobData?.isCarRental}>
                  <Heading size={4}>{i18n.t('entity.orderRequest.labels.workItems')}</Heading>
                </Hide>
                <Hide when={props.isReadOnly || !showAddButtons}>
                  <HStack justify="space-between" align="center" spacing={2}>
                    <Button
                      variant="ghostLink"
                      leftIcon="content/add_circle"
                      onClick={handleOpenAddLabour}
                      data-testid={suffixTestId('addWork', props)}
                      isDisabled={!!props.jobData?.servicePackageDetails?.isLock || !canAddLabour}
                      title={i18n.t('entity.addWork.actions.addWork')}
                    />
                    <Button
                      variant="ghostLink"
                      leftIcon="content/add_circle"
                      onClick={handleOpenAddMaterialRequest}
                      data-testid={suffixTestId('addMaterial', props)}
                      isDisabled={!!props.jobData?.servicePackageDetails?.isLock || !canAddMaterial}
                      title={i18n.t('entity.addMaterial.actions.addMaterial')}
                    />
                  </HStack>
                </Hide>
              </HStack>
              <Hide when={props.jobData?.isCarRental}>
                <Space vertical={4} />
              </Hide>
              <JobItemsDataGrid
                key={props.datagridKey}
                jobId={props.jobData?.id}
                orderId={props.orderId}
                serviceCaseId={props.serviceCaseId}
                datagridKey={addLabourJobId ?? addMaterialRequestJobId}
                requestName={props.jobData?.name}
                data-testid={suffixTestId('items', props)}
              />
            </Show>
          </Show>

          <Show when={currentSegment === JobSegments.FULLY_RETURNED}>
            <Separator />
            <FullyReturnedItemsDataGrid
              serviceCaseId={props.serviceCaseId}
              orderId={props.orderId}
              jobId={props.jobData?.id}
              fullyReturnedItemsCount={props.jobData?.serviceItems?.fullyReturnedItemsCount ?? 0}
              data-testid={suffixTestId('fullyReturnedItems', props)}
            />
          </Show>
        </Box>
      </Show>
    </Card>
  );
}
